import React, { Component, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { useHistory, useLocation, withRouter } from 'react-router-dom/cjs/react-router-dom.min';
import { useConfiguration } from '../../context/configurationContext';
import { IconSearch, IconSpinner, LayoutSingleColumn, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import facebookImage from '../../assets/biketribe-facebook-sharing-1200x630.jpg';
import twitterImage from '../../assets/biketribe-twitter-sharing-600x314.jpg';
import { connect, useDispatch, useSelector } from 'react-redux';
import { compose } from 'redux';
import { withViewport } from '../../util/uiHelpers';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import css from './BlogPage.module.css';
import Icons from '../../components/Icons/Icons';
import BlogCard from './BlogCard/BlogCard';
import {
  fetchAllBlogs,
  getBlogsInProgress,
  getBlogsSelector,
  getBlogsTagsSelector,
} from '../../ducks/strapi.duck';
import { isArrayLength } from '../../util/genericHelpers';
import routeConfiguration from '../../routing/routeConfiguration';
import { createResourceLocatorString } from '../../util/routes';
import { parse } from '../../util/urlHelpers';
import BlogTabs from './BlogTabs';
import classNames from 'classnames';

export const BlogPageComponent = props => {
  const { intl, scrollingDisabled, history, viewport } = props;

  const config = useConfiguration();
  const dispatch = useDispatch();
  const location = useLocation();

  const search = parse(location.search);

  const currentTag = search?.tag;

  useEffect(() => {
    dispatch(fetchAllBlogs({ tag: search?.tag }));
  }, [search?.tag]);

  const blogsData = useSelector(getBlogsSelector) || [];

  const blogsTags = useSelector(getBlogsTagsSelector) || [];
  const searchInProgress = useSelector(getBlogsInProgress);

  const [toggleFilter, setToggleFilter] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [sortBy, setSortBy] = useState('recent'); // Default sorting by most recent

  // Effect to filter and sort blogs based on search keyword and sorting option
  useEffect(() => {
    let processedBlogs = [...blogsData];

    // Filter by search keyword
    if (searchKeyword) {
      processedBlogs = processedBlogs.filter(blog => {
        const title = blog.title.toLowerCase();
        const keyword = searchKeyword.toLowerCase();
        return title.includes(keyword);
      });
    }

    // Sort blogs
    if (sortBy === 'recent') {
      processedBlogs.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    } else if (sortBy === 'relevance') {
      // Example: Sort by a hypothetical 'relevanceScore' property
      processedBlogs.sort((a, b) => b.relevanceScore - a.relevanceScore);
    }

    setFilteredBlogs(processedBlogs);
  }, [searchKeyword, sortBy, blogsData]); // Dependencies

  const blogs = isArrayLength(filteredBlogs) ? filteredBlogs : blogsData;

  // Handler for search input changes
  const handleSearchChange = event => {
    setSearchKeyword(event.target.value);
  };

  const handleSortChange = event => {
    setSortBy(event.target.value);
  };

  const onClickFilter = () => {
    setToggleFilter(!toggleFilter);
  };

  const isMobileLayout = viewport.width > 0 && viewport.width < 768;
  const schemaImage = `${config.marketplaceRootURL}${facebookImage}`;
  const siteTitle = config.marketplaceName;

  const schemaTitle = intl.formatMessage({ id: 'BlogPage.schemaTitle' }, { siteTitle });
  const schemaDescription = intl.formatMessage({
    id: 'BlogPage.schemaDescription',
  });

  const renderBlogTags = isArrayLength(blogsTags)
    ? blogsTags.map(b => {
        return (
          <span
            className={classNames({
              [css.active]: currentTag === b,
            })}
            key={b}
            role="button"
            onClick={() =>
              history.push(
                createResourceLocatorString('BlogPage', routeConfiguration(), {}, { tag: b })
              )
            }
          >
            {b}
          </span>
        );
      })
    : null;

  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      description={schemaDescription}
      title={siteTitle}
      facebookImages={[{ url: facebookImage, width: 1200, height: 630 }]}
      twitterImages={[
        {
          url: `${config.marketplaceRootURL}${twitterImage}`,
          width: 600,
          height: 314,
        },
      ]}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        description: schemaDescription,
        name: schemaTitle,
        image: [schemaImage],
      }}
    >
      <LayoutSingleColumn className={css.pageRoot} topbar={<TopbarContainer />}>
        <div className={css.pageContent}>
          <div className={css.blogsContainer}>
            <div className={css.blogsContent}>
              <div className={css.blogHeader}>
                <span>Our Blog</span>
                <h2>The latest writings from our team</h2>
                <p>The latest industry news, interviews, technologies, and resources.</p>
                <div className={css.searchForm}>
                  <IconSearch />
                  <input
                    type="text"
                    placeholder="Search"
                    value={searchKeyword}
                    onChange={handleSearchChange}
                  />
                </div>
              </div>
              <div className={css.mainBlogWrapper}>
                <div className={css.blogFilterSec}>
                  <BlogTabs
                    isMobileLayout={isMobileLayout}
                    onClickFilter={onClickFilter}
                    toggleFilter={toggleFilter}
                    renderBlogTags={renderBlogTags}
                    currentTag={currentTag}
                  />
                  <div className={css.blogFilterSorting}>
                    <select name="sortby" id="sortby" value={sortBy} onChange={handleSortChange}>
                      <option value="recent">Most Recent</option>
                      <option value="relevance">Relevance</option>
                    </select>
                  </div>
                </div>
                <div className={css.mainBlogContent}>
                  {searchInProgress ? (
                    <IconSpinner />
                  ) : isArrayLength(blogs) ? (
                    blogs.map((blog, index) => <BlogCard key={index} blog={blog} />)
                  ) : (
                    <p>No blog posts found.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const { bool } = PropTypes;

BlogPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  return {
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const mapDispatchToProps = dispatch => ({
  onUpdateProfile: data => dispatch(updateProfile(data)),
});

const BlogPage = compose(
  withViewport,
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(BlogPageComponent);

export default BlogPage;
